import Vue from 'vue'
import { isStaticFirstRender } from './feature-flags'
import type * as InteractiveModule from './tiptap-editor-interactive'

type Importer = () => Promise<typeof InteractiveModule>

const defaultImporter: Importer = () => import('./tiptap-editor-interactive')

let interactive: typeof InteractiveModule | null = null
let loadPromise: Promise<void> | null = null
let importer: Importer = defaultImporter

/** Reactive, so a computed that fell back to core-only output while the chunk was missing re-renders when it arrives. */
export const interactiveLoadState = Vue.observable({ loaded: false })

/** Test seam: forgets a loaded chunk so a test can hold, fail or release the import with its own importer. */
export function resetInteractiveEditorExtensionsForTests(next: Importer = defaultImporter): void {
  interactive = null
  loadPromise = null
  importer = next
  lastFailureAt = Number.NEGATIVE_INFINITY
  interactiveLoadState.loaded = false
}

export function isInteractiveEditorExtensionsLoaded(): boolean {
  return interactive !== null
}

export function getInteractiveEditorExtensionsModule(): typeof InteractiveModule | null {
  return interactive
}

/** Loads slash commands, mentions, and variable autocomplete (Vue + tippy). No-op once loaded. */
export function prefetchInteractiveEditorExtensions(): Promise<void> {
  if (interactive) return Promise.resolve()
  if (!loadPromise) {
    loadPromise = importer()
      .then(module => {
        interactive = module
        interactiveLoadState.loaded = true
      })
      .catch(error => {
        loadPromise = null
        throw error
      })
  }
  return loadPromise
}

export async function ensureInteractiveEditorExtensions(): Promise<void> {
  await prefetchInteractiveEditorExtensions()
}

/** A page can hold hundreds of editors and renders; after a failed load they must not each refetch the chunk. */
const RETRY_COOLDOWN_MS = 5_000
let lastFailureAt = Number.NEGATIVE_INFINITY

/**
 * For synchronous callers that cannot wait: starts (or retries) the load and swallows its failure, which is logged.
 * They carry on with the core extensions and pick the chunk up on a later call, at most once per cooldown.
 */
export function kickInteractiveEditorExtensionsRetry(): void {
  if (interactive || loadPromise) return
  if (Date.now() - lastFailureAt < RETRY_COOLDOWN_MS) return
  prefetchInteractiveEditorExtensions().catch(error => {
    lastFailureAt = Date.now()
    console.error('[tiptap-editor] interactive extensions failed to load; retrying on later use', error)
  })
}

/** Fire-and-forget when static_first_render is off so the first sync createEditor sees the chunk. */
export function prefetchInteractiveEditorExtensionsWhenEager(): void {
  if (isStaticFirstRender()) return
  prefetchInteractiveEditorExtensions().catch(error =>
    console.error('[tiptap-editor] interactive extensions failed to load; they are retried on first use', error)
  )
}
